import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Image, ArrowLeft, Plus, Edit2, Trash2, Eye, EyeOff,
  RefreshCw, GripVertical, Users, User, Link, Calendar, Upload, Loader2, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';

const API = window.location.origin + '/api';
const BACKEND_URL = window.location.origin;

export default function Banners() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    target_audience: 'all',
    is_active: true,
    start_date: '',
    end_date: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setIsUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/admin/banners/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Construct full URL for display
      const imageUrl = response.data.image_url;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/banners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(response.data.banners || []);
    } catch (error) {
      toast.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.image_url) {
      toast.error('Please fill in title and image URL');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingBanner) {
        await axios.put(`${API}/admin/banners/${editingBanner.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner updated successfully');
      } else {
        await axios.post(`${API}/admin/banners`, {
          ...formData,
          position: banners.length
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      fetchBanners();
    } catch (error) {
      toast.error('Failed to save banner: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggle = async (bannerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/admin/banners/${bannerId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to toggle banner');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      image_url: '',
      link_url: '',
      target_audience: 'all',
      is_active: true,
      start_date: '',
      end_date: ''
    });
    setEditingBanner(null);
  };

  const openEditDialog = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      image_url: banner.image_url || '',
      link_url: banner.link_url || '',
      target_audience: banner.target_audience || 'all',
      is_active: banner.is_active !== false,
      start_date: banner.start_date || '',
      end_date: banner.end_date || ''
    });
    setShowDialog(true);
  };

  const getAudienceBadge = (audience) => {
    const styles = {
      all: 'bg-purple-100 text-purple-700',
      customer: 'bg-blue-100 text-blue-700',
      provider: 'bg-green-100 text-green-700'
    };
    const icons = {
      all: <Users className="w-3 h-3" />,
      customer: <User className="w-3 h-3" />,
      provider: <User className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[audience] || styles.all}`}>
        {icons[audience]}
        {audience === 'all' ? 'All Users' : audience.charAt(0).toUpperCase() + audience.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Sidebar */}
      <AdminSidebar />
      <AdminMobileNav />
      
      {/* Main content with sidebar offset */}
      <div className="md:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10 md:top-0 mt-14 md:mt-0">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button onClick={() => navigate('/admin')} className="p-2 hover:bg-slate-100 rounded-lg md:hidden" data-testid="back-btn">
                  <ArrowLeft className="w-5 h-5 text-slate-600" />
                </button>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
                    <Image className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                    Banner Management
                  </h1>
                  <p className="text-xs sm:text-sm text-slate-500">Manage promotional banners for dashboards</p>
                </div>
              </div>
              <div className="flex gap-2 ml-auto sm:ml-0">
                <Button onClick={fetchBanners} variant="outline" size="sm" className="gap-2" data-testid="refresh-btn">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Button 
                  onClick={() => { resetForm(); setShowDialog(true); }} 
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-500 gap-2"
                  data-testid="add-banner-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Banner</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase">Total Banners</p>
            <p className="text-2xl font-bold text-slate-900">{banners.length}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <p className="text-xs text-green-600 uppercase">Active</p>
            <p className="text-2xl font-bold text-green-700">{banners.filter(b => b.is_active).length}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-xs text-blue-600 uppercase">Customer Banners</p>
            <p className="text-2xl font-bold text-blue-700">{banners.filter(b => b.target_audience === 'customer' || b.target_audience === 'all').length}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <p className="text-xs text-purple-600 uppercase">Provider Banners</p>
            <p className="text-2xl font-bold text-purple-700">{banners.filter(b => b.target_audience === 'provider' || b.target_audience === 'all').length}</p>
          </div>
        </div>

        {/* Banners Grid */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
            <p className="mt-2 text-slate-500">Loading banners...</p>
          </div>
        ) : banners.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
            <Image className="w-12 h-12 mx-auto text-slate-300" />
            <p className="mt-2 text-slate-500">No banners yet</p>
            <p className="text-sm text-slate-400">Create your first promotional banner</p>
            <Button 
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="mt-4 bg-indigo-600 hover:bg-indigo-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Banner
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {banners.map((banner, index) => (
              <div 
                key={banner.id} 
                className={`bg-white rounded-xl border overflow-hidden transition-all ${
                  banner.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'
                }`}
                data-testid={`banner-card-${banner.id}`}
              >
                {/* Banner Image */}
                <div className="relative aspect-[2/1] bg-slate-100">
                  <img
                    src={banner.image_url?.startsWith('/') ? `${BACKEND_URL}${banner.image_url}` : banner.image_url}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://placehold.co/800x400?text=Banner'; }}
                  />
                  <div className="absolute top-2 left-2 flex gap-1">
                    <span className="px-2 py-0.5 bg-black/50 text-white text-xs rounded-full">
                      #{index + 1}
                    </span>
                    {!banner.is_active && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>

                {/* Banner Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{banner.title}</h3>
                      <div className="mt-1">
                        {getAudienceBadge(banner.target_audience)}
                      </div>
                    </div>
                  </div>

                  {banner.link_url && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                      <Link className="w-3 h-3" />
                      <span className="truncate">{banner.link_url}</span>
                    </div>
                  )}

                  {(banner.start_date || banner.end_date) && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {banner.start_date ? new Date(banner.start_date).toLocaleDateString() : 'Now'} - 
                        {banner.end_date ? new Date(banner.end_date).toLocaleDateString() : 'Ongoing'}
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggle(banner.id)}
                      className="flex-1"
                      data-testid={`toggle-banner-${banner.id}`}
                    >
                      {banner.is_active ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {banner.is_active ? 'Hide' : 'Show'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(banner)}
                      data-testid={`edit-banner-${banner.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(banner.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-banner-${banner.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-indigo-600" />
              {editingBanner ? 'Edit Banner' : 'Add New Banner'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title">Banner Title *</Label>
              <Input
                id="title"
                placeholder="Summer Sale, New Features, etc."
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1"
                data-testid="banner-title-input"
              />
            </div>

            <div>
              <Label htmlFor="image_url">Banner Image *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="image_url"
                  placeholder="Image URL or upload file..."
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="flex-1"
                  data-testid="banner-image-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap"
                  data-testid="banner-upload-btn"
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Recommended size: 1200x400px. Max 5MB.</p>
              {formData.image_url && (
                <div className="mt-2 relative inline-block">
                  <img 
                    src={formData.image_url.startsWith('/') ? `${BACKEND_URL}${formData.image_url}` : formData.image_url} 
                    alt="Preview" 
                    className="w-full max-w-md h-32 object-cover rounded-lg border border-slate-200"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="link_url">Link URL (optional)</Label>
              <Input
                id="link_url"
                placeholder="https://example.com/promo"
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                className="mt-1"
                data-testid="banner-link-input"
              />
              <p className="text-xs text-slate-500 mt-1">Users will be redirected when clicking the banner</p>
            </div>

            <div>
              <Label htmlFor="target_audience">Target Audience</Label>
              <Select 
                value={formData.target_audience} 
                onValueChange={(v) => setFormData({ ...formData, target_audience: v })}
              >
                <SelectTrigger className="mt-1" data-testid="banner-audience-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="customer">Customers Only</SelectItem>
                  <SelectItem value="provider">Providers Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date (optional)</Label>
                <Input
                  id="start_date"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="mt-1"
                  data-testid="banner-start-date"
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date (optional)</Label>
                <Input
                  id="end_date"
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="mt-1"
                  data-testid="banner-end-date"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded text-indigo-600"
                data-testid="banner-active-checkbox"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Active (show banner immediately)</Label>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-500"
              disabled={isSubmitting}
              data-testid="save-banner-btn"
            >
              {isSubmitting ? 'Saving...' : editingBanner ? 'Update Banner' : 'Create Banner'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
